/**
 * v0.17 taxclass-light (2026-09-14): the single ⓘ info icon + tooltip that
 * carries the mode-aware PST/GST detail every "Tax class" dropdown used to
 * spell out inline (and truncate). QuickBooks/Xero convention: short at the
 * line (the dropdown just shows the class name), detail in one place (this
 * tooltip). Shared by InvoiceDetailPage's two line-item table headers and
 * ProjectCategoriesTab's default-tax-class control so the wording can never
 * drift between surfaces -- see taxClassTooltipCopy (lib/taxClasses.ts).
 *
 * A real <button> (not a bare <span>) so the Radix Tooltip beneath it opens
 * on keyboard focus as well as hover/pointer, and an aria-label gives screen
 * readers a name a "ⓘ" glyph alone would not.
 */
import { Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';
import { taxClassTooltipCopy, type TaxEngineContext } from '@/lib/taxClasses';

interface TaxClassInfoTooltipProps {
  /** Tenant tax-engine status; undefined/null reads as "not live" (fail-closed). */
  context?: TaxEngineContext | null;
  /** Distinguishes this icon's testid when more than one renders on a page
   *  (InvoiceDetailPage renders one per line-item table). */
  testId?: string;
}

export function TaxClassInfoTooltip({ context, testId }: TaxClassInfoTooltipProps) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-label="What does tax class control?"
            data-testid={testId ?? 'tax-class-info-icon'}
          >
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent>
          <p className="max-w-xs">{taxClassTooltipCopy(context)}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
